
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { verifyToken } from "@/services/authService";
import { User } from "./types";
import { isTokenExpired } from "@/services/auth/tokenUtils";

// Global state to prevent multiple simultaneous token verifications and implement circuit breaker
let globalVerificationState = {
  isVerifying: false,
  verificationPromise: null as Promise<User> | null,
  circuitBreakerOpen: false,
  lastFailureTime: 0,
  consecutiveFailures: 0
};

const CIRCUIT_BREAKER_THRESHOLD = 3;
const CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const mountedRef = useRef(true);

  // Sync authLoading flag to localStorage for cross-component coordination
  useEffect(() => {
    if (isLoading) {
      localStorage.setItem('authLoading', 'true');
    } else {
      localStorage.removeItem('authLoading');
    }
  }, [isLoading]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (!mountedRef.current) return;
      
      setIsLoading(true);
      
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("useAuthState: No token found in localStorage.");
          if (mountedRef.current) {
            setUser(null);
            localStorage.removeItem("user");
            setIsLoading(false);
          }
          return;
        }

        // Check if token is expired before verification
        if (isTokenExpired(token)) {
          console.log("useAuthState: Token is expired, clearing session");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (mountedRef.current) {
            setUser(null);
            setIsLoading(false);
          }
          
          const publicPaths = ["/login", "/register", "/forgot-password", "/"];
          if (!publicPaths.includes(location.pathname)) {
            toast({
              title: "Session Expired",
              description: "Your session has expired. Please log in again.",
              variant: "destructive",
            });
            navigate('/login', { replace: true });
          }
          return;
        }
        
        // Check circuit breaker
        const now = Date.now();
        if (globalVerificationState.circuitBreakerOpen) {
          if (now - globalVerificationState.lastFailureTime < CIRCUIT_BREAKER_TIMEOUT) {
            console.log("useAuthState: Circuit breaker open, using cached user");
            const cachedUserJson = localStorage.getItem("user");
            if (cachedUserJson && mountedRef.current) {
              try {
                const cachedUser = JSON.parse(cachedUserJson);
                setUser(cachedUser);
                setIsLoading(false);
                return;
              } catch (parseError) {
                console.error("useAuthState: Failed to parse cached user");
              }
            }
            if (mountedRef.current) {
              setUser(null);
              setIsLoading(false);
            }
            return;
          } else {
            // Reset circuit breaker after timeout
            globalVerificationState.circuitBreakerOpen = false;
            globalVerificationState.consecutiveFailures = 0;
          }
        }
        
        // Skip verification if user was just set from login (cached in localStorage)
        const cachedUserJson = localStorage.getItem("user");
        const loginJustCompleted = localStorage.getItem("loginJustCompleted");
        if (cachedUserJson && loginJustCompleted) {
          try {
            const cachedUser = JSON.parse(cachedUserJson);
            if (cachedUser?.id && mountedRef.current) {
              console.log("useAuthState: Using user from recent login, skipping verify-token");
              setUser(cachedUser);
              setIsLoading(false);
              localStorage.removeItem("loginJustCompleted");
              return;
            }
          } catch {}
          localStorage.removeItem("loginJustCompleted");
        }

        console.log("useAuthState: Token found, verifying...");
        
        // Check if verification is already in progress
        if (globalVerificationState.isVerifying && globalVerificationState.verificationPromise) {
          console.log("useAuthState: Token verification already in progress, waiting...");
          try {
            const userData = await globalVerificationState.verificationPromise;
            if (mountedRef.current) {
              setUser(userData);
              localStorage.setItem("user", JSON.stringify(userData));
            }
            return;
          } catch (err) {
            // Fall through to start new verification
            console.log("useAuthState: Previous verification failed, starting new one");
          }
        }

        // Start new verification
        globalVerificationState.isVerifying = true;
        globalVerificationState.verificationPromise = verifyToken(token);

        try {
          const userData = await globalVerificationState.verificationPromise;
          console.log("useAuthState: Token verified successfully.");
          if (mountedRef.current) {
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
          }
          // Reset failure count on success
          globalVerificationState.consecutiveFailures = 0;
        } finally {
          globalVerificationState.isVerifying = false;
          globalVerificationState.verificationPromise = null;
        }

      } catch (error: any) {
        console.error("useAuthState: Failed to verify token:", error);
        const errorMessage = String(error.message || '');

        // Increment failure count and check circuit breaker
        globalVerificationState.consecutiveFailures++;
        if (globalVerificationState.consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
          globalVerificationState.circuitBreakerOpen = true;
          globalVerificationState.lastFailureTime = Date.now();
          console.log("useAuthState: Circuit breaker opened due to consecutive failures");
        }

        if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch') || errorMessage.includes('timeout')) {
          console.warn("useAuthState: Network error during token verification. Using cached user if available.");
          const cachedUserJson = localStorage.getItem("user");
          if (cachedUserJson && mountedRef.current) {
            try {
              const cachedUser = JSON.parse(cachedUserJson);
              setUser(cachedUser);
              console.log("useAuthState: Loaded user from localStorage due to network error.");
            } catch (parseError) {
              console.error("useAuthState: Failed to parse cached user from localStorage.");
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              if (mountedRef.current) setUser(null);
            }
          } else {
            if (mountedRef.current) setUser(null);
          }
        } else if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('token verification failed') || errorMessage.toLowerCase().includes('token has expired')) {
          console.log('useAuthState: Authentication failed (401 or token invalid), redirecting to login.');
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (mountedRef.current) setUser(null);
          
          const publicPaths = ["/login", "/register", "/forgot-password", "/"];
          if (!publicPaths.includes(location.pathname)) {
            toast({
              title: "Session Expired",
              description: "Please log in again to continue.",
              variant: "destructive",
            });
            navigate('/login', { replace: true });
          }
        } else {
          console.warn("useAuthState: Unexpected error during token verification. Clearing session.", error);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (mountedRef.current) setUser(null);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    
    loadUser();
  }, [navigate, toast]);

  useEffect(() => {
    if (location.pathname === '/settings') {
      const savedTab = localStorage.getItem('settingsTab');
      if (savedTab) {
        console.log("useAuthState: Navigated to /settings, savedTab:", savedTab);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    console.log("useAuthState: User state changed:", user);
    console.log("useAuthState: isLoading state changed:", isLoading);
  }, [user, isLoading]);

  return {
    user,
    setUser,
    isLoading,
    navigate,
    toast
  };
};
